/**
 * Admin Login Component
 *
 * Desktop login for /admin. Verifies email/password against POST /api/auth/admin/login,
 * and offers forgot-password (emails a temporary password).
 */
import React, { useState } from 'react';
import './login.css';
import AdminAuthShell from './AdminAuthShell';
import { loginAdmin, requestAdminPasswordReset } from '../../../services/adminAuthApi';
import { notifyAdminAuthChanged, setAdminSession } from '../auth/adminAuthStorage';

type LoginView = 'login' | 'forgot' | 'forgot-sent';

export default function AdminLogin() {
  const [view, setView] = useState<LoginView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginAdmin(email.trim(), password);
      setAdminSession(result.email, result.mustChangePassword);
      notifyAdminAuthChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const message = await requestAdminPasswordReset(email.trim());
      setInfo(message);
      setView('forgot-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const goToForgot = () => {
    setError(null);
    setInfo(null);
    setPassword('');
    setView('forgot');
  };

  const goToLogin = () => {
    setError(null);
    setInfo(null);
    setView('login');
  };

  return (
    <AdminAuthShell>
      {view === 'login' && (
        <form className="admin-login__form" onSubmit={handleLogin}>
          <input
            type="email"
            className="admin-login__input"
            placeholder="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <input
            type="password"
            className="admin-login__input"
            placeholder="パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {error && <p className="admin-login__error" role="alert">{error}</p>}
          <button type="submit" className="admin-login__btn" disabled={submitting}>
            {submitting ? '確認中…' : 'ログイン'}
          </button>
          <button type="button" className="admin-login__link" onClick={goToForgot}>
            パスワードをお忘れですか？
          </button>
        </form>
      )}

      {view === 'forgot' && (
        <form className="admin-login__form" onSubmit={handleForgot}>
          <p className="admin-login__hint">
            登録済みのメールアドレスを入力してください。仮パスワードを送信します。ログイン後に新しいパスワードへ変更してください。
          </p>
          <input
            type="email"
            className="admin-login__input"
            placeholder="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          {error && <p className="admin-login__error" role="alert">{error}</p>}
          <button type="submit" className="admin-login__btn" disabled={submitting}>
            {submitting ? '送信中…' : '仮パスワードを送信'}
          </button>
          <button type="button" className="admin-login__btn admin-login__btn--secondary" onClick={goToLogin}>
            ログインへ戻る
          </button>
        </form>
      )}

      {view === 'forgot-sent' && (
        <div className="admin-login__form">
          <p className="admin-login__success" role="status">
            {info || 'ご入力のメールアドレスが登録されている場合、仮パスワードを送信しました。'}
          </p>
          <p className="admin-login__hint">
            メールに記載の仮パスワードでログインし、続けて新しいパスワードを設定してください。
          </p>
          <button type="button" className="admin-login__btn" onClick={goToLogin}>
            ログインへ戻る
          </button>
        </div>
      )}
    </AdminAuthShell>
  );
}
